import { useCallback, useEffect, useState } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useNavigate } from 'react-router';
import { Grid, Paper, Typography, Tabs, Tab } from '@mui/material';
import { Box } from '@mui/system';
import CircularProgress from '@mui/material/CircularProgress';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import { useAnalyticContext } from '../../../../hooks/analyticContext';
import { configureStackedBarChartForEdVisit } from '../../../ChartConfiguration/ChartConfiguration';
import ClaimsPerformance from '../../../../Models/Claims/ClaimsPreview.model';
import TopTenMembers from '../../../../Models/Claims/TopTenMembers.model';
import FilterOptions from '../DetailFilterOption';
import { getEdVisitGraph, getTopTenEdVisitMembers} from '../AnalyticService';
import { ClaimAnalyticEnum } from '../../../../Enum/ClaimAnalyticEnum';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

const EdVisitDetail = () => {
    const history = useNavigate();
    const [isFetched, setIsFetched] = useState<boolean>(false);
    const [isGraphShow, setIsGraphShow] = useState<boolean>(false);
    const [isMemberShow, setIsMemberShow] = useState<boolean>(false);
    const [isPrepared, setIsPrepared] = useState<boolean>(false);
    const [monthChartOptions, setMonthChartOptions] = useState({});
    const [graphSummary, setGraphSummary] = useState<ClaimsPerformance[]>([]);
    const [topTenMembers, setTopTenMembers] = useState<TopTenMembers[]>([]);
    const [selectedYear, setSelectedYear] = useState(0);
    const [paginationModel, setPaginationModel] = useState({ pageSize: 10, page: 0 });

    const {
        edHealthPlanIds,
        setEdHealthPlanIds,
        edYears,
        setEdYears,
        edMonths,
        setEdMonths,
        edTeam,
        setEdTeam,
        showEdInActiveTeams,
        setShowEdInActiveTeams,
    } = useAnalyticContext();

    const initialPage = useCallback(async () => {
        if (isFetched) {
            return;
        }

        if (!isGraphShow) {
            setMonthChartOptions(
                configureStackedBarChartForEdVisit(
                    'ED Visit',
                    '',
                    '',
                    'Visit',
                    [{ name: '', data: [0], showInLegend: false, title: { text: null } }],
                    [0],
                    '',
                    history
                )
            );
        }

        setIsFetched(true);
    }, [isFetched, setIsFetched, isGraphShow, history]);

    useEffect(() => {
        initialPage();
    }, [initialPage]);

    const changeFilter = async () => {
        if (edHealthPlanIds && edHealthPlanIds.length > 0) {
            setIsGraphShow(false);
            setIsMemberShow(false);
            const data = await getEdVisitGraph(
                edHealthPlanIds,
                edTeam,
                edYears,
                edMonths,
                showEdInActiveTeams
            );
            setGraphSummary(data);
            setIsPrepared(true);
            setIsGraphShow(true);

            const members = await getTopTenEdVisitMembers(
                edHealthPlanIds,
                edTeam,
                edYears,
                edMonths,
                showEdInActiveTeams
            );
            setTopTenMembers(members);
            setIsMemberShow(true);
        }
    };

    useEffect(() => {
        if (!isFetched && edHealthPlanIds && edHealthPlanIds.length > 0) {
            changeFilter();
            setIsFetched(true);
        }
        initialPage();
        // eslint-disable-next-line react-hooks/exhaustive-deps;
    }, [edHealthPlanIds]);

    useEffect(() => {
        if (graphSummary && isPrepared) {

            let xAxisValues = graphSummary.map(x => x.Category);

            let yAxisValues = [
                {
                    name: 'ED Visit',
                    data: graphSummary.map((x) => ({
                        y: x.TotalVisit,
                        total: x.MemberCount
                    })),
                    color: '#6495ED',
                    showInLegend: false
                }
            ];

            setMonthChartOptions(
                configureStackedBarChartForEdVisit(
                    'ED Visit',
                    '',
                    '',
                    'Visits',
                    yAxisValues,
                    xAxisValues,
                    'EdVisit',
                    history
                )
            );

            setIsGraphShow(true);
            setIsPrepared(false);
        }
    }, [graphSummary, edHealthPlanIds, isPrepared, history]);

    const columns: GridColDef[] = [
        {
            field: 'Rank',
            headerName: '#',
            maxWidth: 70,
        },
        {
            field: 'MemberName',
            headerName: 'Member Name',
            flex: 1,
        },
        {
            field: 'Cost',
            headerName: 'Visits',
            flex: 1,
            valueGetter: (value, row) => `${row.Cost}`,
        },
    ];

    const navigateToGraph = () => {
        history('/provider/metricsummary/smart-dashboard');
    };

    return (
        <Paper elevation={5}>
            <Grid container spacing={2} paddingLeft={2} paddingRight={2} paddingTop={1}>
                <Grid size={12} paddingBottom={1}>
                    <span onClick={navigateToGraph}>
                        <ArrowBackIcon className="pointer mr-2" style={{ color: 'black' }} titleAccess="Back" />
                    </span>
                    <Typography display={'inline-block'} variant="h6" color={'primary'}>
                        Emergency Department Visits
                    </Typography>
                </Grid>
                <FilterOptions
                    healthPlanIds={edHealthPlanIds}
                    setHealthPlanIds={setEdHealthPlanIds}
                    years={edYears}
                    setYears={setEdYears}
                    months={edMonths}
                    setMonths={setEdMonths}
                    team={edTeam}
                    setTeam={setEdTeam}
                    showInActiveTeams={showEdInActiveTeams}
                    setShowInActiveTeams={setShowEdInActiveTeams}
                    onFetch={changeFilter}
                    claimType={ClaimAnalyticEnum.EdVisit}
                />
                <Grid size={5} marginTop={1} sx={{ position: 'relative' }}>
                    {!isGraphShow ? (
                        <Box className="loader-center">
                            <CircularProgress />
                        </Box>
                    ) : null}
                    <Grid container>
                        <Grid size={12} marginTop={2} style={{ backgroundColor: '#F5F5F5' }} paddingY={1} paddingLeft={2} paddingRight={2}>
                            <Typography display={'inline-block'} variant="h6" color={'primary'}>
                                Emergency Department Visits
                            </Typography>
                        </Grid>
                    </Grid>
                    <HighchartsReact highcharts={Highcharts} options={monthChartOptions} />
                </Grid>
                <Grid size={1}></Grid>
                <Grid size={6} marginTop={1}>
                    <Grid container>
                        <Grid size={12} marginTop={2} style={{ backgroundColor: '#F5F5F5' }} paddingY={1} paddingLeft={2} paddingRight={2}>
                            <Typography display={'inline-block'} variant="h6" color={'primary'}>
                                Top 10 Members
                            </Typography>
                        </Grid>
                    </Grid>
                    <Grid container spacing={2} paddingLeft={2} paddingRight={2} paddingBottom={2}>
                        <Grid size={12} marginTop={2} minHeight="40vh" sx={{ position: 'relative' }}>
                            {!isGraphShow || !isMemberShow ? (
                                <Box className="loader-center">
                                    <CircularProgress />
                                </Box>
                            ) : (
                                <>
                                    <Tabs
                                        value={selectedYear}
                                        onChange={(e, newValue) => setSelectedYear(newValue)}
                                        variant="scrollable"
                                        scrollButtons="auto"
                                    >
                                        {topTenMembers?.map((detail, index) => (
                                            <Tab key={index} label={detail.Year === '0' ? "Baseline" : detail.Year} />
                                        ))}
                                    </Tabs>
                                    <Grid spacing={2}>
                                        {topTenMembers?.map((detail, index) => {
                                            const rowsWithRank = detail.Members.map((m, i) => ({
                                                ...m,
                                                Rank: i + 1,
                                            }));
                                            return (
                                                selectedYear === index && (
                                                    <Grid container key={`detail-${index}`}>
                                                        <Grid size={12} sx={{ height: '55vh', width: '100%' }}>
                                                            <div style={{ width: '100%', height: '55vh', overflow: 'auto' }}>
                                                                <DataGrid
                                                                    rows={rowsWithRank}
                                                                    columns={columns}
                                                                    paginationModel={paginationModel}
                                                                    onPaginationModelChange={setPaginationModel}
                                                                    pageSizeOptions={[5]}
                                                                    getRowId={(row) => row.Id + row.AhcccsId}
                                                                    getRowHeight={() => 45}
                                                                    autoHeight={false}
                                                                    sx={{
                                                                        height: '100%',
                                                                        [`& .MuiDataGrid-cell`]: { py: 1 },
                                                                    }}
                                                                />
                                                            </div>
                                                        </Grid>
                                                    </Grid>
                                                )
                                            )
                                        })}
                                    </Grid>
                                </>
                            )}
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </Paper>
    );
};

export default EdVisitDetail;
